import { SignalCitation, SignalType } from 'api/signal/types';

export interface Participant {
  emailId: string;
  userId?: number;
  photoUrl?: string;
  firstName?: string;
  lastName?: string;
  userType?: 'EXTERNAL' | 'NATIVE' | 'PARTNER';
  accountId?: number;
  identityType: 'USER';
  loginType?: 'USER';
}

export interface MeetingMeta {
  participants: Participant[];
}

export interface ActionItem {
  actionItem: string;
  discussedAt: string;
  actionItemAssignedTo: string;
}

export interface KeyTakeaway {
  score: number;
  scoreReason: string;
  keyTakeAwayContent: string;
}

export interface TopicSummary {
  topic: string;
  description: string;
}

export interface CustomerSentiment {
  [customerName: string]: string;
}

export interface MeetingSentiment {
  overallSentimentSummary: string;
  customerSentimentBreakdown: CustomerSentiment[];
}

export interface MeetingSummaryContent {
  nextSteps: string[];
  actionItems: {
    [assignee: string]: ActionItem[];
  };
  meetingSummary: {
    meetingSummaryShort: TopicSummary[];
  };
  meetingSentiment: MeetingSentiment;
  meetingKeyTakeaways: {
    keyTakeaways: KeyTakeaway[];
  };
  meetingDetailedSummary: {
    meetingSummaryDetailed: TopicSummary[];
  };
}

export interface MeetingSummary {
  meetingSummaryId: number;
  externalMeetingId: number;
  transcriptId: number;
  accountId: number;
  meetingSummaryStatus: 'COMPLETED' | 'IN_PROGRESS';
  summary: MeetingSummaryContent;
}

export interface SignalField {
  fieldId: number;
  fieldName: string;
  fieldType: string;
  fieldDataType: string;
  description: string;
  customField: boolean;
  optionsAvailable: boolean;
  fieldValue: number;
  userCreated: boolean;
  parentFieldName: string;
  objectType: string;
  objectSubType: string;
  metaData: {
    options: Array<{
      color: string;
      label: string;
      value: number;
      position: number;
      active: boolean;
      deletable: boolean;
      editable: boolean;
    }>;
    rangeAvailable: boolean;
    captureKeyIntervalForFirstActivityOnly: boolean;
    allowAdminsAndOwnersToEdit: boolean;
  };
  fieldLabel: string;
  createdBy: number;
  isEnabled: boolean;
  private: boolean;
}

export enum SignalSource {
  MEETING = 'MEETING',
  EMAIL = 'EMAIL',
}

export interface SignalOccurrence {
  signalOccurrenceId: number;
  signalConfigurationId: number;
  sourceId: number;
  sourceType: SignalSource;
  companyId: number;
  accountId: number;
  title: string;
  summary: string;
  metaData: {
    [key: string]: number;
  };
  createdAt: Date;
  signalName: string;
  reportedByUsers: ReportedBy[];
  signalType: SignalType;
  fields: SignalField[];
  reportedBy: ReportedBy[];
  citationMapping: SignalCitation;
}

export interface DetectedSignal {
  signalConfigurationId: number;
  accountId: number;
  signalName: string;
  signalType: SignalType;
}

export interface Timeline {
  accountId: number;
  startTime: string;
  meta: MeetingMeta;
  updatedAt: string;
  createdAt: string;
  source: {
    type: SignalSource;
    id: number;
    title: string;
  };
  summary: string;
  detectedSignals: DetectedSignal[];
  signalOccurrences: SignalOccurrence[];
  summaryAvailable: boolean;
  signalAwareRecap: string;
}

export interface ReportedBy {
  emailId: string;
  userId: number;
  firstName: string;
  lastName: string;
  userName: string;
  identityType: string;
  role: {
    roleName: string;
  };
  company?: {
    companyName?: string;
    companyLogoUrl?: string;
    companyId?: number;
  };
}

export interface GroupedTimelines {
  [date: string]: Timeline[];
}
